/**
 * Estilo de capas (paint expressions Mapbox).
 * Hex CALIBRADOS para coincidir con tokens.css `--st-*` de @aosenuma/ui.
 * Si tocas tokens.css, sincroniza estos valores a mano (Mapbox no puede leer
 * CSS variables en paint expressions).
 */

export const COLOR = {
  // Semaforo aosenuma - 5 fases en familia de marca (alineado a tokens.css).
  st_gris:  '#8896a0',
  st_azul:  '#6394c0',
  st_verde: '#3a9c7e',
  st_lime:  '#d2dc76',
  st_rojo:  '#b56556',
  // Acentos
  lime: '#d2dc76',
  teal: '#4A9CA5',
  teal_bright: '#66B8C0',
  cream: '#F2F4ED',
  rose: '#B87A6A',
  cream_mid_a: 'rgba(209, 214, 204, 0.40)',
  white_dim: 'rgba(255,255,255,0.18)',
  orange: '#F97316',
  violet: '#A855F7',
  amber:  '#FBBF24',
  coral:  '#FB7185',
  /** AOI San Pedro unificado — azul acero cenizo (sin componente magenta; RGB ~31,85,110). */
  aoi_san_pedro: '#1F556E',
} as const;

/**
 * Relleno base de parcelas cuando aún no hay feature-state del inventario (o el
 * overlay de semáforo es transparente). Neutro, legible sobre imagen satelital.
 */
export const fillColorParcelBase = 'rgba(136, 150, 160, 0.40)' as const;

/**
 * Contorno por defecto (sin `firmado` en feature-state). Contrasta con fondo satelital.
 */
export const lineColorParcelDefault = 'rgba(242, 244, 237, 0.62)' as const;

/**
 * Builders de expresiones de paint/filter parametrizados por la fuente del
 * estado vivo. Mapbox-gl rechaza expresiones que mezclan `['get', x]` y
 * `['feature-state', x]` dentro del mismo `coalesce` (validate_expression
 * lanza TypeError silencioso → el Layer se monta sin filter). Mantenemos
 * dos branches puros:
 *
 *   - `enriched=true`  → properties (`['get', 'live_*']`) — usado cuando el
 *     caller pasa `parcelasGeoJSON` ya enriquecido (Dashboard).
 *   - `enriched=false` → feature-state (`['feature-state', '*']`) — usado
 *     cuando el caller solo pasa `parcelasState` (landing/equipo, legacy).
 *
 * Precedencia (audit Fase 5): `firmado=true` SIEMPRE pinta el color de
 * contratada, aunque el estado raw siga siendo `en_negociacion`.
 *
 * Mapeo de colores (2026-05-11 swap): contratada=verde, en_negociacion=lime.
 * Sincronizar con --color-contratada / --color-en-negociacion de tokens.css.
 */

function srcEstado(enriched: boolean): unknown {
  return enriched
    ? ['coalesce', ['get', 'live_estado'], 'sin_prospectar']
    : ['coalesce', ['feature-state', 'estado'], 'sin_prospectar'];
}

function srcFirmado(enriched: boolean): unknown {
  return enriched
    ? ['coalesce', ['get', 'live_firmado'], false]
    : ['coalesce', ['feature-state', 'firmado'], false];
}

function srcIrregular(enriched: boolean): unknown {
  return enriched
    ? ['coalesce', ['get', 'live_irregular'], false]
    : ['coalesce', ['feature-state', 'irregular'], false];
}

function srcReunionBucket(enriched: boolean): unknown {
  return enriched
    ? ['coalesce', ['get', 'live_reunion_bucket'], 'sin_reunion']
    : ['coalesce', ['feature-state', 'reunion_bucket'], 'sin_reunion'];
}

export function makeFillColorByEstado(enriched: boolean): unknown {
  return [
    'case',
    ['==', srcFirmado(enriched), true], COLOR.st_verde,
    [
      'match',
      srcEstado(enriched),
      'inviable',       COLOR.st_rojo,
      'contratada',     COLOR.st_verde,
      'en_negociacion', COLOR.st_lime,
      'en_valoracion',  COLOR.st_azul,
      'sin_prospectar', COLOR.st_gris,
      'rgba(0,0,0,0)',
    ],
  ];
}

export function makeLineColorByFirmado(enriched: boolean): unknown {
  return [
    'case',
    // Contorno de firmadas = verde (post-swap 2026-05-11; antes era lime).
    ['==', srcFirmado(enriched), true], COLOR.st_verde,
    lineColorParcelDefault,
  ];
}

export function makeLineWidthByFirmado(enriched: boolean): unknown {
  return [
    'case',
    ['==', srcFirmado(enriched), true], 2.4,
    1.1,
  ];
}

export function makeIrregularBoolean(enriched: boolean): unknown {
  return ['==', srcIrregular(enriched), true];
}

export function makeEstadoMatchExpr(enriched: boolean): unknown {
  return srcEstado(enriched);
}

export function makeReunionBucketMatchExpr(enriched: boolean): unknown {
  return srcReunionBucket(enriched);
}
